import { paperReferenceLabel } from './paperInboxReference';
import {
    cleanImportSubject,
    isHiddenRejectedControlCandidate,
    isNativeSourceAlertImport,
    senderLabelForImport,
    UNKNOWN_SENDER_LABEL,
    unresolvedReasonLabel,
    type EmailImportPaperRow,
    type EmailImportRow,
    type MatchedTopicRef,
} from './paperInboxViewModel';
import { getSimplePaperUrl } from './paperUrl';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from './i18n/messages';

export type PaperInboxOutcome = 'matched' | 'no_match' | 'unresolved' | 'processing';

export type PaperInboxLogRow = {
    id: string;
    emailImportPaperId: number;
    title: string;
    outcome: PaperInboxOutcome;
    referenceLabel: string | null;
    unresolvedReason: MessageDescriptor | null;
    paperId: string | null;
    paperzillaUrl: string | null;
    sourceUrl: string | null;
    sourceId: number | null;
    sourcePaperId: string | null;
    doi: string | null;
    matchedTopics: MatchedTopicRef[];
    candidate: EmailImportPaperRow;
    importRow: EmailImportRow;
};

export type PaperInboxImportSummary = {
    counts: {
        matched: number;
        noMatch: number;
        unresolved: number;
        processing: number;
    };
    emptyMessage: LocalizedMessage;
};

export type PaperInboxImportItem = {
    id: string;
    importRow: EmailImportRow;
    title: string;
    senderLabel: string;
    rows: PaperInboxLogRow[];
    summary: PaperInboxImportSummary;
};

export function paperInboxPresentedOutcome(
    outcome: PaperInboxOutcome,
    _activeTopicCount: number
): PaperInboxOutcome {
    return outcome;
}

export function paperInboxPaperCount(item: PaperInboxImportItem): number {
    return Math.max(item.importRow.visible_candidate_count ?? item.rows.length, 0);
}

export function paperInboxPaperCountLabel(item: PaperInboxImportItem): LocalizedMessage {
    if (isNativeSourceAlertImport(item.importRow)) {
        return '';
    }
    const count = paperInboxPaperCount(item);
    return {
        ...msg({
        id: 'inbox.paperCount',
        message: '{count, plural, one {# paper} other {# papers}}',
        }),
        values: { count },
    };
}

export function paperInboxImportMetadataLabel(item: PaperInboxImportItem): LocalizedMessage {
    if (isNativeSourceAlertImport(item.importRow)) {
        return item.senderLabel === UNKNOWN_SENDER_LABEL
            ? msg({ id: 'inbox.unknownSender', message: 'Unknown sender' })
            : item.senderLabel;
    }
    if (item.senderLabel === UNKNOWN_SENDER_LABEL) {
        return {
            ...msg({ id: 'inbox.unknownSenderMetadata', message: 'Unknown sender • {count, plural, one {# paper} other {# papers}}' }),
            values: { count: paperInboxPaperCount(item) },
        };
    }
    return {
        ...msg({
        id: 'inbox.importMetadata',
        message: '{sender} • {count, plural, one {# paper} other {# papers}}',
        }),
        values: { sender: item.senderLabel, count: paperInboxPaperCount(item) },
    };
}

export function paperInboxShowsTopicStatus(activeTopicCount: number): boolean {
    return activeTopicCount > 0;
}

export function paperInboxShowsOutcomeBadge(
    outcome: PaperInboxOutcome,
    _activeTopicCount: number
): boolean {
    return outcome === 'unresolved' || outcome === 'processing';
}

function isImportProcessing(importRow: EmailImportRow): boolean {
    return ['queued', 'parsing'].includes(importRow.parse_state || '')
        || ['queued', 'resolving'].includes(importRow.resolution_state || '');
}

function isCandidateProcessing(importRow: EmailImportRow, candidate: EmailImportPaperRow): boolean {
    const resolverState = candidate.resolver_state || '';
    if (candidate.paper_id || ['held', 'failed'].includes(resolverState)) {
        return false;
    }

    return ['queued', 'resolving'].includes(resolverState) || isImportProcessing(importRow);
}

export function paperInboxOutcome(
    importRow: EmailImportRow,
    candidate: EmailImportPaperRow
): PaperInboxOutcome {
    if (isCandidateProcessing(importRow, candidate)) {
        return 'processing';
    }

    if (!candidate.paper_id || ['held', 'failed'].includes(candidate.resolver_state || '')) {
        return 'unresolved';
    }

    if ((candidate.matchedTopics?.length ?? 0) > 0) {
        return 'matched';
    }

    return 'no_match';
}

function buildPaperInboxLogRow(importRow: EmailImportRow, candidate: EmailImportPaperRow): PaperInboxLogRow {
    const outcome = paperInboxOutcome(importRow, candidate);
    // i18n-ignore: technical-token -- sentinel is translated at the rendering boundary.
    const title = candidate.paper?.title ?? candidate.raw_title ?? candidate.raw_url ?? 'Untitled paper';
    const row: PaperInboxLogRow = {
        id: `paper-${candidate.id}`,
        emailImportPaperId: candidate.id,
        title,
        outcome,
        referenceLabel: null,
        unresolvedReason: outcome === 'unresolved' ? unresolvedReasonLabel(candidate) : null,
        paperId: candidate.paper_id,
        paperzillaUrl: candidate.paper_id ? getSimplePaperUrl(candidate.paper_id, title) : null,
        sourceUrl: candidate.canonical_url ?? candidate.raw_url ?? null,
        sourceId: candidate.paper?.source_id ?? null,
        sourcePaperId: candidate.paper?.source_paper_id ?? null,
        doi: candidate.paper?.doi ?? null,
        matchedTopics: candidate.matchedTopics ?? [],
        candidate,
        importRow,
    };

    row.referenceLabel = paperReferenceLabel(row);
    return row;
}

function summarizeImport(importRow: EmailImportRow, rows: PaperInboxLogRow[]): PaperInboxImportSummary {
    const counts = {
        matched: 0,
        noMatch: 0,
        unresolved: 0,
        processing: 0,
    };

    for (const row of rows) {
        if (row.outcome === 'matched') counts.matched += 1;
        if (row.outcome === 'no_match') counts.noMatch += 1;
        if (row.outcome === 'unresolved') counts.unresolved += 1;
        if (row.outcome === 'processing') counts.processing += 1;
    }

    const emptyMessage = isNativeSourceAlertImport(importRow)
        ? msg({ id: 'inbox.empty.nativeSource', message: 'Paperzilla already tracks this source directly, so this alert was logged without importing individual links.' })
        : isImportProcessing(importRow)
        ? msg({ id: 'inbox.empty.processing', message: 'We received this email and Paperzilla is still processing its links.' })
        : msg({ id: 'inbox.empty.noLinks', message: 'We couldn’t find paper links in this email.' });

    return {
        counts,
        emptyMessage,
    };
}

export function buildPaperInboxImportItems(
    imports: EmailImportRow[],
    candidates: EmailImportPaperRow[]
): PaperInboxImportItem[] {
    const importsById = new Map(imports.map((row) => [row.id, row]));
    const rowsByImportId = new Map<number, PaperInboxLogRow[]>();

    for (const candidate of candidates) {
        if (isHiddenRejectedControlCandidate(candidate)) {
            continue;
        }

        const importRow = importsById.get(candidate.email_import_id);
        if (!importRow) {
            continue;
        }

        const list = rowsByImportId.get(importRow.id) ?? [];
        list.push(buildPaperInboxLogRow(importRow, candidate));
        rowsByImportId.set(importRow.id, list);
    }

    const sortedImports = [...imports].sort((left, right) => {
        const timestampDelta = new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
        if (timestampDelta !== 0) {
            return timestampDelta;
        }

        return right.id - left.id;
    });

    return sortedImports.map((importRow) => ({
        id: `email-${importRow.id}`,
        importRow,
        title: cleanImportSubject(importRow.subject),
        senderLabel: senderLabelForImport(importRow),
        rows: rowsByImportId.get(importRow.id) ?? [],
        summary: summarizeImport(importRow, rowsByImportId.get(importRow.id) ?? []),
    }));
}
